import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Box, Typography, Button, IconButton, Paper, useMediaQuery, useTheme
} from '@mui/material';
import {
    LayoutDashboard, Calendar, MessageCircle, HelpCircle, User, Shield, Lock, CreditCard, 
    DollarSign, Share2, Film, Plus, LogOut, ChevronLeft, ChevronRight, Menu, X
} from 'lucide-react';
import './Sidebar.css';

const Sidebar = ({ onToggle, darkMode }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [isOpen, setIsOpen] = useState(!isMobile);
    const location = useLocation();
    const sidebarRef = useRef(null);
    const menuBtnRef = useRef(null);
    
    // Update isOpen state when screen size changes
    useEffect(() => {
        setIsOpen(!isMobile);
    }, [isMobile]);

    // Close sidebar when clicking outside (mobile only)
    useEffect(() => {
        if (!isMobile) return;
        
        const handleClickOutside = (event) => {
            if (sidebarRef.current && 
                !sidebarRef.current.contains(event.target) && 
                menuBtnRef.current && 
                !menuBtnRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isMobile]);

    // Close sidebar when route changes (mobile only)
    useEffect(() => {
        if (isMobile) {
            setIsOpen(false);
        }
    }, [location, isMobile]);
    
    // Toggle sidebar function
    const toggleSidebar = () => {
        setIsOpen(!isOpen);
        if (onToggle) {
            onToggle();
        }
    };
    
    // Menu items definition
    const menuItems = [
        {
            group: "Main",
            items: [
                { icon: LayoutDashboard, label: 'Dashboard', href: '/my-profile' }
            ]
        },
        {
            group: "Profile",
            items: [
                { icon: User, label: 'Personal Info', href: '/edit-personalinfo' },
                { icon: Shield, label: 'Verification', href: '/profile-verification' },
                { icon: Lock, label: 'Password', href: '/change-password' }
            ]
        },
        {
            group: "Financial",
            items: [
                { icon: CreditCard, label: 'Payment', href: '/payment-history' },
                { icon: DollarSign, label: 'Payout', href: '/payout' }
            ]
        },
        {
            group: "Features",
            items: [
                { icon: Share2, label: 'Referral', href: '/referral' }
            ]
        },
        {
            group: "Support",
            items: [
                { 
                    icon: MessageCircle, 
                    label: 'Submit Ticket', 
                    href: '/support'  // New internal route
                },
                { 
                    icon: HelpCircle, 
                    label: 'Help Center', 
                    href: 'https://circledin.zendesk.com/hc/en-us',
                    external: true 
                }
            ]
        },
        {
            group: "Account",
            items: [
                { icon: LogOut, label: 'Logout', href: '/logout' }
            ]
        }
    ];

    // MOBILE SIDEBAR RENDER with inline styles for guaranteed spacing
    if (isMobile) {
        // Inline styles for greater control
        const styles = {
            hamburgerButton: {
                position: 'fixed',
                bottom: '24px',
                right: '24px',
                zIndex: 1500,
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: '#FF4775',
                color: 'white',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(255, 71, 117, 0.5)',
                cursor: 'pointer'
            },
            overlay: {
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(2px)',
                WebkitBackdropFilter: 'blur(2px)',
                zIndex: 1300
            },
            sidebar: {
                position: 'fixed',
                top: 0,
                right: 0,
                width: '58%',
                maxWidth: '320px',
                height: '100%',
                backgroundColor: '#102c36',
                zIndex: 1400,
                transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', sans-serif`,
                overflow: 'hidden',
                color: '#ffffff'
            },
            header: {
                height: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 16px',
                backgroundColor: '#0c1f24'
            },
            brand: {
                fontSize: '1.5rem',
                fontWeight: 700,
                letterSpacing: '-0.01em',
                background: 'linear-gradient(45deg, #FF4775, #FF7676)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textDecoration: 'none',
                textShadow: '0 1px 3px rgba(255, 71, 117, 0.1)'
            },
            closeButton: {
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                cursor: 'pointer'
            },
            content: {
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                padding: '16px 0'
            },
            sectionTitle: {
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#8A9CAA',
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                padding: '20px 16px 5px',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
            },
            sectionLine: {
                width: '20px',
                height: '3px',
                backgroundColor: '#FF4775',
                marginRight: '8px',
                borderRadius: '1.5px',
            },
            menuItem: {
                display: 'flex',
                alignItems: 'center',
                padding: '16px',
                color: '#ffffff',
                textDecoration: 'none',
                position: 'relative',
                transition: 'all 0.2s ease',
                marginBottom: '16px',  // Force extra space between items
                borderRadius: '2px'
            },
            activeMenuItem: {
                color: '#FF4775',
                backgroundColor: 'rgba(255, 71, 117, 0.15)'
            },
            menuIcon: {
                color: '#ffffff',
                marginRight: '16px',
                opacity: 0.9
            },
            activeMenuIcon: {
                color: '#FF4775',
                opacity: 1
            },
            menuLabel: {
                fontSize: '1rem',
                letterSpacing: '0.01em',
                fontWeight: 500
            },
            externalLink: {
                marginLeft: '6px',
                fontSize: '0.75rem',
                opacity: 0.7
            },
            accountHeader: {
                padding: '16px',
                fontSize: '0.75rem',
                letterSpacing: '0.1em',
                color: 'rgba(255, 255, 255, 0.6)',
                marginTop: '24px',
                fontWeight: 500,
                textAlign: 'center',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                paddingTop: '24px'
            },
            accountMenu: {
                paddingBottom: '16px',
                marginTop: '8px'
            },
            logoutButton: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '14px 24px',
                margin: '24px 16px 0',
                backgroundColor: '#FFFFFF',
                color: '#000000',
                borderRadius: '8px',
                textDecoration: 'none',
                textAlign: 'center',
                fontWeight: 500,
                fontSize: '1rem',
                width: 'calc(100% - 32px)'
            },
            logoutIcon: {
                color: '#000000',
                marginRight: '8px'
            }
        };

        return (
            <>
                {/* Mobile Hamburger Menu Button */}
                <button 
                    ref={menuBtnRef}
                    style={styles.hamburgerButton}
                    onClick={toggleSidebar}
                    aria-label="Toggle menu"
                >
                    <Menu size={24} />
                </button>
                
                {/* Overlay - only shown when sidebar is open */}
                {isOpen && (
                    <div 
                        style={styles.overlay}
                        onClick={toggleSidebar} 
                    />
                )}
                
                {/* Mobile sidebar */}
                <div 
                    ref={sidebarRef}
                    style={styles.sidebar}
                >
                    {/* Header with Logo and Close Button */}
                    <div style={styles.header}>
                        <Link to="/" style={styles.brand}>
                            Circledin
                        </Link>
                        <button 
                            style={styles.closeButton}
                            onClick={toggleSidebar}
                            aria-label="Close menu"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Menu Content */}
                    <div style={styles.content}>
                        {/* Render all sections before "ACCOUNT" */}
                        {menuItems.slice(0, 5).map((group, idx) => (
                            <div key={idx} style={{marginTop: idx > 0 ? '20px' : '0'}}>
                                {/* Section Title */}
                                <div style={styles.sectionTitle}>
                                    <div style={styles.sectionLine}></div>
                                    {group.group.toUpperCase()}
                                </div>
                                
                                {/* Section Items */}
                                {group.items.map((item, itemIdx) => {
                                    const isActive = location.pathname === item.href;
                                    
                                    const MenuLink = item.external ? 'a' : Link;
                                    const linkProps = item.external ? {
                                        href: item.href,
                                        target: "_blank",
                                        rel: "noopener noreferrer"
                                    } : {
                                        to: item.href
                                    };
                                    
                                    return (
                                        <MenuLink
                                            key={itemIdx}
                                            {...linkProps}
                                            style={{
                                                ...styles.menuItem,
                                                ...(isActive ? styles.activeMenuItem : {})
                                            }}
                                            onClick={() => {
                                                if (!item.external) setIsOpen(false);
                                            }}
                                        >
                                            <item.icon 
                                                size={20} 
                                                style={{
                                                    ...styles.menuIcon,
                                                    ...(isActive ? styles.activeMenuIcon : {})
                                                }} 
                                            />
                                            <span style={styles.menuLabel}>{item.label}</span>
                                            {item.external && <span style={styles.externalLink}>↗</span>}
                                        </MenuLink>
                                    );
                                })}
                            </div>
                        ))}
                        
                        {/* YOUR ACCOUNT Section */}
                        <div style={styles.accountHeader}>
                            YOUR ACCOUNT
                        </div>
                        
                        {/* Account Items */}
                        <div style={styles.accountMenu}>
                            {menuItems.slice(5).map((group, idx) => (
                                <div key={idx}>
                                    {group.items.map((item, itemIdx) => {
                                        // Special styling for logout button
                                        if (item.label === 'Logout') {
                                            return (
                                                <Link
                                                    key={itemIdx}
                                                    to={item.href}
                                                    style={styles.logoutButton}
                                                    onClick={() => setIsOpen(false)}
                                                >
                                                    <item.icon size={20} style={styles.logoutIcon} />
                                                    <span>{item.label}</span>
                                                </Link>
                                            );
                                        }
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // DESKTOP SIDEBAR RENDER
    return (
        <Paper
            elevation={0}
            className={`circledin-sidebar ${!isOpen ? 'collapsed' : ''} ${darkMode ? 'dark' : ''}`}
            sx={{
                width: isOpen ? 240 : 80,
                transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                height: '100vh',
                position: 'fixed',
                left: 0,
                top: 0,
                zIndex: 1200,
                borderRadius: 0,
                overflow: 'hidden',
                bgcolor: 'transparent'
            }}
        >
            {/* Header with Logo */}
            <Box className="sidebar-header">
                {isOpen && (
                    <Link to="/" className="brand">
                        Circledin
                    </Link>
                )}
                <IconButton 
                    onClick={toggleSidebar}
                    sx={{
                        width: 32,
                        height: 32,
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: darkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 71, 117, 0.08)'
                        }
                    }}
                >
                    {isOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
                </IconButton>
            </Box>

            {/* Content */}
            <Box 
                className="sidebar-content"
                sx={{ 
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    height: 'calc(100vh - 64px)',
                    pb: 2
                }}
            >
                {menuItems.map((group, idx) => (
                    <Box 
                        key={idx} 
                        className="sidebar-section"
                        sx={{ mb: 1 }}
                    >
                        {isOpen && (
                            <Typography
                                className="sidebar-group"
                                sx={{
                                    px: 3,
                                    py: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase'
                                }}
                            >
                                {group.group}
                            </Typography>
                        )}
                        {group.items.map((item, itemIdx) => {
                            const isActive = location.pathname === item.href;
                            
                            const MenuLink = item.external ? 'a' : Link;
                            const linkProps = item.external ? {
                                href: item.href,
                                target: "_blank",
                                rel: "noopener noreferrer"
                            } : {
                                to: item.href
                            };
                            
                            return (
                                <MenuLink
                                    key={itemIdx}
                                    {...linkProps}
                                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                                    title={item.label} // Add tooltip on hover
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        whiteSpace: 'nowrap', // Prevent text wrapping
                                        width: isOpen ? 'calc(100% - 30px)' : '50px',
                                        mx: isOpen ? '15px' : 'auto',
                                        px: isOpen ? '14px' : '12px',
                                        py: '10px',
                                        borderRadius: '10px',
                                        justifyContent: isOpen ? 'flex-start' : 'center',
                                        fontSize: '0.875rem',
                                        fontWeight: isActive ? 600 : 500,
                                        transition: 'all 0.25s ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 71, 117, 0.08)',
                                            transform: 'translateX(4px)'
                                        },
                                        '&.active': {
                                            background: 'linear-gradient(90deg, rgba(255, 71, 117, 0.08), rgba(255, 71, 117, 0.01))',
                                        }
                                    }}
                                >
                                    <Box
                                        component={item.icon}
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            color: isActive ? '#FF4775' : darkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
                                            strokeWidth: 1.5,
                                            flexShrink: 0,
                                            mr: isOpen ? 1.5 : 0,
                                            transition: 'all 0.2s ease'
                                        }}
                                    />
                                    {isOpen && (
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontSize: '0.875rem',
                                                fontWeight: 'inherit',
                                                color: 'inherit',
                                                letterSpacing: '0.01em',
                                                lineHeight: 1.4,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                    )}
                                </MenuLink>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        </Paper>
    );
};

export default Sidebar;